import z from 'zod';

import { normalizeBusinessIdForUsage } from 'app-components/flux/accounts/accountsUtils';
import type { ReceiptSplitSeed } from 'app-components/flux/transactions/splitsHelpers';
import { httpClientInterface } from 'app-components/utils/core';
import { getServiceUrl } from 'app-components/utils/core/interfaces/httpClientInterface';

import { clearReceiptSplitSuggestionCache, getReceiptSplitSuggestion } from './receiptSplitSuggestionCache';
import {
  type CreateReceiptPayload,
  type FetchReceiptsParams,
  type FetchReceiptsResponse,
  type LinkReceiptToTransactionPayload,
  RECEIPTS_DEFAULT_PAGE_SIZE,
  type Receipt,
  type ReceiptFeedbackReason,
  type ReceiptPageParams,
  ReceiptSchema,
  type ReceiptSummary,
  ReceiptSummarySchema,
  type SubmitReceiptFeedbackPayload,
  type UpdateReceiptPayload,
} from './receiptsTypes';

const ReceiptResponseSchema = z
  .union([
    ReceiptSchema,
    z.object({
      resource: ReceiptSchema,
      id: z.string().optional(),
      clientId: z.string().optional(),
    }),
  ])
  .transform((response) => {
    if ('resource' in response) {
      return response.resource;
    }

    return response;
  });

/**
 * Legacy receipt detail responses can return `documentId: null` because the original persisted receipt model allowed
 * receipts without a storage document. When the list response already supplied a valid document id, preserve that
 * known value before strict schema parsing so an otherwise successful detail or feedback response remains usable.
 */
const preserveLegacyDetailDocumentId = (response: unknown, fallbackDocumentId?: string): unknown => {
  if (!fallbackDocumentId || typeof response !== 'object' || response === null) return response;

  if ('resource' in response && typeof response.resource === 'object' && response.resource !== null) {
    if ('documentId' in response.resource && response.resource.documentId === null) {
      return { ...response, resource: { ...response.resource, documentId: fallbackDocumentId } };
    }
    return response;
  }

  if ('documentId' in response && response.documentId === null) {
    return { ...response, documentId: fallbackDocumentId };
  }

  return response;
};

const LegacyLinkedReceiptDetailSchema = z.object({
  id: z.string(),
  documentId: z.null(),
  transactionId: z.string().min(1),
});

const GetReceiptsResponseSchema = z
  .object({
    resources: z.array(ReceiptSchema),
    metaData: z
      .object({
        pageSize: z.number().optional(),
        limit: z.number().optional(),
        asOf: z.string().optional(),
        lastRefId: z.string().optional(),
        currentPage: z.number().optional(),
        offset: z.number().optional(),
        totalPages: z.number().optional(),
        totalSize: z.number().optional(),
      })
      .passthrough()
      .optional(),
    totalElements: z.number().default(0),
    totalPages: z.number().default(0),
  })
  .transform((response) => ({
    ...response,
    totalElements: response.totalElements || response.metaData?.totalSize || 0,
    totalPages: response.totalPages || response.metaData?.totalPages || 0,
  }));

type ReceiptsQueryParams = {
  modifiedAfter?: string;
  page?: number;
  size?: number;
  sort?: string;
  status?: FetchReceiptsParams['status'];
  transactionId?: string;
};

type CreateReceiptRequest = {
  documentId: string;
  name?: string | null;
};

type UpdateReceiptRequest = Omit<UpdateReceiptPayload, 'id'>;
type SubmitReceiptFeedbackRequest = {
  rating: SubmitReceiptFeedbackPayload['rating'];
  reasons?: ReceiptFeedbackReason[];
  comment?: string;
};

export type ReceiptSplitSuggestionUsageType = 'PERSONAL' | 'BUSINESS';

export type GenerateReceiptSplitSuggestionsPayload = {
  usageType: ReceiptSplitSuggestionUsageType;
};

export const getReceiptSplitSuggestionUsageType = (
  businessId: string | null | undefined,
): ReceiptSplitSuggestionUsageType => (normalizeBusinessIdForUsage(businessId) ? 'BUSINESS' : 'PERSONAL');

const ReceiptSplitSuggestionSchema = z.object({
  amount: z.number().finite(),
  split: z.object({
    items: z
      .array(
        z.object({
          name: z.string().min(1),
          amount: z.number().finite(),
          coa: z.discriminatedUnion('type', [
            z.object({ type: z.literal('CATEGORY'), id: z.string().min(1) }),
            z.object({ type: z.literal('UNCATEGORIZED'), id: z.literal('0') }),
          ]),
        }),
      )
      .min(2),
  }),
});

const buildQueryParams = (params: FetchReceiptsParams = {}): ReceiptsQueryParams => ({
  modifiedAfter: params.modifiedAfter,
  page: params.page,
  size: params.size,
  sort: params.sort,
  status: params.status,
  transactionId: params.transactionId,
});

export const getHttpErrorStatus = (error: unknown): number | undefined => {
  if (typeof error !== 'object' || error === null || !('response' in error)) {
    return undefined;
  }

  const { response } = error as { response?: { status?: unknown } };
  return typeof response?.status === 'number' ? response.status : undefined;
};

const buildCreateReceiptRequest = (payload: CreateReceiptPayload): CreateReceiptRequest => ({
  documentId: payload.documentId,
  ...(payload.name !== undefined ? { name: payload.name } : {}),
});

const buildUpdateReceiptRequest = ({
  merchant,
  transactionDate,
  total,
  currency,
  memo,
}: UpdateReceiptPayload): UpdateReceiptRequest => ({
  ...(merchant !== undefined ? { merchant } : {}),
  ...(transactionDate !== undefined ? { transactionDate } : {}),
  ...(total !== undefined ? { total } : {}),
  ...(currency !== undefined ? { currency } : {}),
  ...(memo !== undefined ? { memo } : {}),
});

const buildSubmitReceiptFeedbackRequest = ({
  rating,
  reasons,
  comment,
}: SubmitReceiptFeedbackPayload): SubmitReceiptFeedbackRequest => ({
  rating,
  ...(reasons !== undefined ? { reasons } : {}),
  ...(comment !== undefined ? { comment } : {}),
});

const resolveReceiptDetail = async (id: string, response: unknown, fallbackDocumentId?: string): Promise<Receipt> => {
  if (!fallbackDocumentId) {
    const resource =
      typeof response === 'object' && response !== null && 'resource' in response ? response.resource : response;
    const legacyDetail = LegacyLinkedReceiptDetailSchema.safeParse(resource);
    if (legacyDetail.success && legacyDetail.data.id === id) {
      // A cold attachment lookup has no cached document ID. Recover it from this transaction's
      // list projection, which retains storage IDs for legacy receipts, rather than loading history.
      const linkedReceipts = await receiptsApi.fetchAll({ transactionId: legacyDetail.data.transactionId });
      fallbackDocumentId = linkedReceipts.resources.find((receipt) => receipt.id === id)?.documentId;
    }
  }
  return ReceiptResponseSchema.parse(preserveLegacyDetailDocumentId(response, fallbackDocumentId));
};

export const receiptsApi = {
  async create(payload: CreateReceiptPayload): Promise<Receipt> {
    const requestUrl = `${getServiceUrl()}/receipts`;
    return ReceiptResponseSchema.parse(
      (await httpClientInterface.send('POST', requestUrl, buildCreateReceiptRequest(payload))).data,
    );
  },

  async fetch(params?: FetchReceiptsParams): Promise<FetchReceiptsResponse> {
    const requestUrl = `${getServiceUrl()}/receipts`;
    return GetReceiptsResponseSchema.parse(
      (await httpClientInterface.send('GET', requestUrl, undefined, undefined, buildQueryParams(params))).data,
    );
  },

  async fetchPage({ reviewState, page, pageSize, sort }: ReceiptPageParams): Promise<FetchReceiptsResponse> {
    return GetReceiptsResponseSchema.parse(
      (
        await httpClientInterface.send('GET', `${getServiceUrl()}/receipts`, undefined, undefined, {
          reviewState,
          page: page + 1,
          size: pageSize,
          sort: `${sort.field},${sort.direction}`,
        })
      ).data,
    );
  },

  async fetchSummary(): Promise<ReceiptSummary> {
    return ReceiptSummarySchema.parse(
      (await httpClientInterface.send('GET', `${getServiceUrl()}/receipts/summary`)).data,
    );
  },

  async fetchByDocumentId(documentId: string): Promise<Receipt | undefined> {
    const response = GetReceiptsResponseSchema.parse(
      (await httpClientInterface.send('GET', `${getServiceUrl()}/receipts`, undefined, undefined, { documentId })).data,
    );
    return response.resources[0];
  },

  async fetchAll(params: FetchReceiptsParams = {}): Promise<FetchReceiptsResponse> {
    if (params.page !== undefined) {
      return receiptsApi.fetch(params);
    }

    const size = params.size ?? RECEIPTS_DEFAULT_PAGE_SIZE;
    const firstPage = await receiptsApi.fetch({ ...params, page: 1, size });
    const totalPages = Math.max(firstPage.totalPages, firstPage.metaData?.totalPages ?? 0);
    const resources = [...firstPage.resources];

    for (let page = 2; page <= totalPages; page += 1) {
      const response = await receiptsApi.fetch({ ...params, page, size });
      resources.push(...response.resources);
    }

    return {
      ...firstPage,
      resources,
    };
  },

  async delete(id: string): Promise<void> {
    const requestUrl = `${getServiceUrl()}/receipts/${id}`;
    await httpClientInterface.send('DELETE', requestUrl);
  },

  async fetchById(id: string, fallbackDocumentId?: string): Promise<Receipt> {
    const requestUrl = `${getServiceUrl()}/receipts/${id}`;
    const response: unknown = (await httpClientInterface.send('GET', requestUrl)).data;
    return resolveReceiptDetail(id, response, fallbackDocumentId);
  },

  async findById(id: string, fallbackDocumentId?: string): Promise<Receipt | undefined> {
    const requestUrl = `${getServiceUrl()}/receipts/${id}`;
    const response = await httpClientInterface.send('GET', requestUrl, undefined, undefined, undefined, {
      // Optional attachment probes often use ordinary document IDs. Accept a miss before the
      // platform's HTTP error interceptor reports it; all other failures still reject normally.
      validateStatus: (status: number): boolean => (status >= 200 && status < 300) || status === 404,
    });
    return response.status === 404 ? undefined : resolveReceiptDetail(id, response.data, fallbackDocumentId);
  },

  async retry(id: string): Promise<Receipt> {
    clearReceiptSplitSuggestionCache(id);
    const requestUrl = `${getServiceUrl()}/receipts/${id}/retry`;
    return ReceiptResponseSchema.parse((await httpClientInterface.send('POST', requestUrl)).data);
  },

  async markViewed(id: string): Promise<Receipt> {
    const requestUrl = `${getServiceUrl()}/receipts/${id}/view`;
    return ReceiptResponseSchema.parse((await httpClientInterface.send('POST', requestUrl)).data);
  },

  async generateSplitSuggestions(
    id: string,
    payload: GenerateReceiptSplitSuggestionsPayload,
    revision: string = '',
  ): Promise<ReceiptSplitSeed> {
    return getReceiptSplitSuggestion(id, payload.usageType, revision, async () => {
      const requestUrl = `${getServiceUrl()}/receipts/${id}/split-suggestions`;
      return ReceiptSplitSuggestionSchema.parse((await httpClientInterface.send('POST', requestUrl, payload)).data);
    });
  },

  clearSplitSuggestionCache: clearReceiptSplitSuggestionCache,

  async update(payload: UpdateReceiptPayload): Promise<Receipt> {
    clearReceiptSplitSuggestionCache(payload.id);
    const requestUrl = `${getServiceUrl()}/receipts/${payload.id}`;
    return ReceiptResponseSchema.parse(
      (await httpClientInterface.send('PUT', requestUrl, buildUpdateReceiptRequest(payload))).data,
    );
  },

  async submitFeedback(payload: SubmitReceiptFeedbackPayload): Promise<Receipt> {
    const requestUrl = `${getServiceUrl()}/receipts/${payload.id}/feedback`;
    return ReceiptResponseSchema.parse(
      (await httpClientInterface.send('PUT', requestUrl, buildSubmitReceiptFeedbackRequest(payload))).data,
    );
  },

  async link({ id, transactionId }: LinkReceiptToTransactionPayload): Promise<Receipt> {
    const requestUrl = `${getServiceUrl()}/receipts/${id}/link`;
    return ReceiptResponseSchema.parse((await httpClientInterface.send('POST', requestUrl, { transactionId })).data);
  },

  async unlink(id: string): Promise<Receipt> {
    const requestUrl = `${getServiceUrl()}/receipts/${id}/unlink`;
    return ReceiptResponseSchema.parse((await httpClientInterface.send('POST', requestUrl)).data);
  },
} as const;
